import React, { useState } from 'react';
import client from '../../../lib/feathers';
import { decodePatch } from '../../../lib/decodeSiteText';

/**
 * Interactive per-CATEGORY Phase-A review. Instead of approving all listing
 * fields or none, the operator checks the sections to accept (name, address,
 * contact+socials, hours, accommodations, GPS) and applies them admin-direct:
 * each section carries a ready-to-apply `patch` (a whole merged sub-object, so
 * a PATCH can't wipe siblings — built by the pipeline in
 * extract_listing_fields._build_phase_a_sections).
 *
 * Apply path: PATCH the listing with the union of selected section patches, then
 * stamp `phaseAReviewedAt` + `phaseAReviewSections` on the run. We deliberately
 * do NOT set phaseAApprovedAt/approvedAt, so the supervisor never re-applies the
 * full autoPatch over our selective choice. If the run has no wines (Phase B),
 * we also flip outcome→ok so the row leaves the queue.
 *
 * proposalData.sections[]: { key, label, changes[], flagged, reasons[], patch }
 */

function fmt(v) {
  if (v === undefined || v === null || v === '') return <span className="text-gray-400 italic">empty</span>;
  if (typeof v === 'object') return <code className="break-all">{JSON.stringify(v)}</code>;
  return <span className="break-all">{String(v)}</span>;
}

// The actual proposed value lives in the section's ready-to-apply `patch`
// (e.g. patch.address.zip). `fill` changes don't carry it on the change row,
// so resolve it by walking the field path into the patch.
function valueAt(obj, path) {
  if (!obj || !path) return undefined;
  return String(path).split('.').reduce(
    (o, k) => (o && typeof o === 'object' ? o[k] : undefined), obj,
  );
}

export default function PhaseASectionReview({ run, sections, hasWines, onDone }) {
  // Default: accept clean (non-flagged) sections; leave flagged ones unchecked
  // so the operator opts into the risky changes (name change, different-location
  // address) deliberately.
  const [selected, setSelected] = useState(
    () => new Set(sections.filter((s) => !s.flagged).map((s) => s.key)),
  );
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (key) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const apply = async () => {
    const chosen = sections.filter((s) => selected.has(s.key));
    // Backstop: never patch a listing with text the site escaped. The pipeline
    // decodes at harvest now, but a row queued before that still holds
    // "Winery &amp; Brewery", and applying it would set that as the real name.
    const merged = decodePatch(Object.assign({}, ...chosen.map((s) => s.patch || {})));
    setBusy(true);
    try {
      if (Object.keys(merged).length && run.listing?.id) {
        await client.service('admin/listing').patch(run.listing.id, merged);
      }
      const patch = {
        phaseAReviewedAt: new Date().toISOString(),
        phaseAReviewSections: {
          applied: chosen.map((s) => s.key),
          dismissed: sections.filter((s) => !selected.has(s.key)).map((s) => s.key),
        },
      };
      // No wines to review → this run is fully handled; clear it from the queue.
      if (!hasWines) {
        patch.outcome = 'ok';
        patch.finishedAt = new Date().toISOString();
      }
      await client.service('admin/listing-enrichment-run').patch(run.id, patch);
      setDone(true);
      if (onDone) onDone(run.id, !hasWines);
    } catch (e) {
      alert(`Apply failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!sections || sections.length === 0) {
    return <div className="text-xs text-gray-400 italic">No listing-field changes proposed.</div>;
  }
  if (done) {
    return <div className="text-xs text-green-700">✓ Listing info applied{hasWines ? ' — wines (Phase B) still below' : ' — run cleared'}.</div>;
  }

  const chosenCount = selected.size;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-xs uppercase tracking-wide text-gray-500">Listing info (Phase A) — accept by category</h4>
        <button
          type="button"
          onClick={apply}
          disabled={busy}
          className="inline-flex items-center px-3 py-1.5 border border-primary rounded-md text-xs font-medium text-primary bg-white hover:bg-primary hover:text-white disabled:opacity-50"
          title="Apply the checked categories to the listing."
        >
          {busy ? 'Applying…' : `Apply selected (${chosenCount})`}
        </button>
      </div>
      <div className="space-y-1">
        {sections.map((s) => {
          const toneCls = s.flagged ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200';
          return (
            <label key={s.key} className={`flex items-start gap-2 text-xs border rounded px-2 py-1.5 cursor-pointer ${toneCls}`}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={selected.has(s.key)}
                onChange={() => toggle(s.key)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-800">{s.label}</span>
                  {s.flagged && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 border border-yellow-300">
                      flagged{s.reasons?.length ? `: ${s.reasons.join(', ')}` : ''}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {(s.changes || []).map((c, i) => {
                    const newVal = (c.new !== undefined && c.new !== null && c.new !== '')
                      ? c.new : valueAt(s.patch, c.field);
                    return (
                      <div key={i} className="font-mono text-[11px] text-gray-600">
                        {c.field}
                        <span className="mx-1 text-gray-400">:</span>
                        {c.kind !== 'fill' && (<>{fmt(c.old)}<span className="mx-1 text-gray-400">→</span></>)}
                        {fmt(newVal)}
                      </div>
                    );
                  })}
                </div>
              </div>
            </label>
          );
        })}
      </div>
      <p className="mt-1 text-[11px] text-gray-400">
        Checked categories are applied to the listing now; unchecked are dismissed (logged for audit). Wines are handled separately below.
      </p>
    </div>
  );
}
